import { Banknote, ChevronDown, ChevronUp } from "lucide-react";
import { useMemo, useState } from "react";

import {
  useTargetEdit,
  type TargetEditPatch,
  type UseTargetEditOptions,
} from "../../../../lib/profile/use-target-edit";
import type { LoanProduct } from "../../../../lib/agent-types";
import { useLoanProducts } from "../../../../lib/loan-products";
import { cn } from "../../../../lib/utils";
import { Select } from "../../../ui/select";

type LoanProgramOption = {
  value: string;
  label: string;
  product: LoanProduct;
  termMo: number;
};

// Fallback list mirrors loanproduct.registry — used before the catalog
// fetch resolves so the dropdown can render an immediate value rather
// than a flash of "loading…". SILL-326 makes the live catalog
// authoritative once fetched; this list is the bootstrap default and
// should stay in sync with backend/internal/services/loanproduct/registry.go.
const FALLBACK_LOAN_PROGRAM_OPTIONS: LoanProgramOption[] = [
  {
    value: "conventional:360",
    label: "Conventional, 30-year fixed",
    product: "conventional",
    termMo: 360,
  },
  {
    value: "conventional:180",
    label: "Conventional, 15-year fixed",
    product: "conventional",
    termMo: 180,
  },
  { value: "fha:360", label: "FHA, 30-year fixed", product: "fha", termMo: 360 },
  { value: "fha:180", label: "FHA, 15-year fixed", product: "fha", termMo: 180 },
  { value: "va:360", label: "VA, 30-year fixed", product: "va", termMo: 360 },
  { value: "va:180", label: "VA, 15-year fixed", product: "va", termMo: 180 },
  { value: "usda:360", label: "USDA, 30-year fixed", product: "usda", termMo: 360 },
  {
    value: "jumbo:360",
    label: "Jumbo, 30-year fixed",
    product: "jumbo",
    termMo: 360,
  },
  {
    value: "jumbo:180",
    label: "Jumbo, 15-year fixed",
    product: "jumbo",
    termMo: 180,
  },
  { value: "arm_7_6:360", label: "ARM 7/6", product: "arm_7_6", termMo: 360 },
];

interface LoanProgramRowProps {
  product: LoanProduct | null;
  termMo: number | null;
  opts: UseTargetEditOptions;
}

export function LoanProgramRow({ product, termMo, opts }: LoanProgramRowProps) {
  const [expanded, setExpanded] = useState(false);
  const editTarget = useTargetEdit(opts);
  const { products: catalog } = useLoanProducts();

  const options = useMemo<LoanProgramOption[]>(() => {
    if (!catalog) return FALLBACK_LOAN_PROGRAM_OPTIONS;
    return catalog
      .filter((entry) => entry.profile_selectable)
      .map((entry) => ({
        value: `${entry.product}:${entry.term_months}`,
        label: entry.label,
        product: entry.product as LoanProduct,
        termMo: entry.term_months,
      }));
  }, [catalog]);

  const selectItems = useMemo(
    () => options.map(({ value, label }) => ({ value, label })),
    [options],
  );

  const current =
    options.find((o) => o.product === product && o.termMo === termMo) ??
    options[0] ??
    FALLBACK_LOAN_PROGRAM_OPTIONS[0];

  const [selectDraftState, setSelectDraftState] = useState({
    base: current.value,
    value: current.value,
  });
  const selectDraft =
    selectDraftState.base === current.value
      ? selectDraftState.value
      : current.value;
  const setSelectDraft = (value: string) => {
    setSelectDraftState({ base: current.value, value });
  };

  const handleCommit = (raw: string) => {
    if (raw === current.value) {
      setSelectDraft(raw);
      return;
    }
    const next = options.find((o) => o.value === raw);
    if (!next) return;
    setSelectDraft(raw);
    const patch: TargetEditPatch = {
      preferred_product: next.product,
      preferred_term_mo: next.termMo,
    };
    editTarget.mutate(patch).catch(() => {
      setSelectDraft(current.value);
    });
  };

  const error = editTarget.error ? "Couldn't save — please try again." : undefined;

  return (
    <article
      id="profile-row-loan_product"
      data-state={expanded ? "open" : "closed"}
      className={cn(
        "overflow-hidden p-0 transition-colors hover:bg-accent/35",
        expanded && "bg-background/60",
      )}
    >
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex min-h-[4.75rem] w-full items-center justify-between gap-3 px-4 py-3 text-left sm:px-5"
        aria-label="Loan program"
        aria-expanded={expanded}
        data-slot="interactive-control"
      >
        <span className="flex min-w-0 items-center gap-3">
          <span className="grid size-10 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
            <Banknote aria-hidden="true" className="size-4" strokeWidth={2} />
          </span>
          <span className="block min-w-0 truncate text-base font-semibold text-foreground">
            Loan program
          </span>
        </span>
        <span className="flex min-w-0 shrink-0 items-center justify-end gap-3">
          <span className="block min-w-0 truncate text-right text-sm font-semibold text-foreground">
            {current.label}
          </span>
          {expanded ? (
            <ChevronUp aria-hidden="true" className="h-4 w-4" />
          ) : (
            <ChevronDown aria-hidden="true" className="h-4 w-4" />
          )}
        </span>
      </button>
      {expanded && (
        <div className="border-t border-border-soft bg-background/60 px-4 pb-4 pt-4 sm:px-5">
          <div className="max-w-xl">
            <Select
              disabled={editTarget.isPending}
              error={error}
              items={selectItems}
              label="Loan program"
              onValueChange={handleCommit}
              placeholder="Select..."
              value={selectDraft}
            />
          </div>
        </div>
      )}
    </article>
  );
}
